import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Card, CardContent, TextField, Button, IconButton, Grid, Divider,
  Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel, Chip, Checkbox,
  Alert, Table, TableHead, TableBody, TableRow, TableCell, Tooltip,
  ListItemText, OutlinedInput, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions } from '@mui/material';
import { Add, Delete, Save, ArrowUpward, ArrowDownward, LockOpen, Replay } from '@mui/icons-material';
import dayjs from 'dayjs';
import { evaluateFormula, buildFormulaVars } from '../../utils/formula';
import { criteriaAPI, formsAPI, priorityAPI, declarationsAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';
import { v4 as uuidv4 } from '../../utils/uuid';

var BLANK_PRIORITY = { enabled: false, field_id: '', mode: 'sort', released_through: 0, tiers: [] };

export default function ReviewCriteria() {
  var params = useParams(); var formId = params.id;
  var sn = useSnackbar(); var notify = sn.enqueueSnackbar;
  var cs = useState([]); var criteria = cs[0]; var setCriteria = cs[1];
  var as = useState(false); var anonymousReview = as[0]; var setAnonymousReview = as[1];
  var rs = useState(true); var reviewOpen = rs[0]; var setReviewOpen = rs[1];
  var hs = useState(true); var hideEmpty = hs[0]; var setHideEmpty = hs[1];
  var vfs = useState([]); var visibleFieldIds = vfs[0]; var setVisibleFieldIds = vfs[1];
  var vss = useState([]); var visibleSectionIds = vss[0]; var setVisibleSectionIds = vss[1];
  var fss = useState(null); var formSchema = fss[0]; var setFormSchema = fss[1];
  var ps = useState(BLANK_PRIORITY); var priority = ps[0]; var setPriority = ps[1];
  var sts = useState(null); var status = sts[0]; var setStatus = sts[1];
  var de = useState(false); var declEnabled = de[0]; var setDeclEnabled = de[1];
  var dt = useState(''); var declTitle = dt[0]; var setDeclTitle = dt[1];
  var dh = useState(''); var declHTML = dh[0]; var setDeclHTML = dh[1];
  var dv = useState(1); var declVersion = dv[0]; var setDeclVersion = dv[1];
  var dl = useState(null); var declLog = dl[0]; var setDeclLog = dl[1];
  var sf = useState(''); var scoreFormula = sf[0]; var setScoreFormula = sf[1];
  var se = useState(false); var formulaEnabled = se[0]; var setFormulaEnabled = se[1];
  var stg = useState([]); var stages = stg[0]; var setStages = stg[1];
  var us = useState(false); var useStages = us[0]; var setUseStages = us[1];

  function loadStatus() {
    priorityAPI.status(formId).then(function(r) { setStatus(r.data.data); }).catch(function() {});
  }

  useEffect(function() {
    criteriaAPI.get(formId).then(function(r) {
      var data = r.data.data;
      setCriteria(data.criteria || []);
      setAnonymousReview(!!data.anonymous_review);
      setReviewOpen(data.review_open !== false);
      setHideEmpty(data.hide_empty_for_reviewers !== false);
      setVisibleFieldIds(data.visible_field_ids || []);
      setVisibleSectionIds(data.visible_section_ids || []);
      var p = data.review_priority || {};
      setPriority(Object.assign({}, BLANK_PRIORITY, p, {tiers: p.tiers || []}));
      setStages(data.review_stages || []);
      setUseStages(!!data.use_review_stages);
      setDeclEnabled(!!data.declaration_enabled);
      setDeclTitle(data.declaration_title || '');
      setDeclHTML(data.declaration_html || '');
      setDeclVersion(data.declaration_version || 1);
      setScoreFormula(data.score_formula || '');
      setFormulaEnabled(!!data.score_formula_enabled);
    });
    formsAPI.get(formId).then(function(r) {
      var s = r.data.data.schema;
      var schema = typeof s === 'string' ? JSON.parse(s) : s;
      if (typeof schema === 'string') { try { schema = JSON.parse(schema); } catch(e2) { schema = {}; } }
      setFormSchema(schema);
    });
    loadStatus();
  }, [formId]);

  function addCriterion() {
    setCriteria(criteria.concat([{ id: uuidv4(), name: '', description: '', max_score: 10,
      weight: 1.0, scoring_type: 'slider', required: true, share_with_applicant: false, options: [] }]));
  }
  function update(idx, field, value) {
    var c = criteria.slice();
    c[idx] = Object.assign({}, c[idx]);
    c[idx][field] = value;
    setCriteria(c);
  }
  function remove(idx) {
    setCriteria(criteria.filter(function(_, i) { return i !== idx; }));
  }

  function toggleField(fieldId) {
    if (visibleFieldIds.indexOf(fieldId) >= 0) {
      setVisibleFieldIds(visibleFieldIds.filter(function(id) { return id !== fieldId; }));
    } else {
      setVisibleFieldIds(visibleFieldIds.concat([fieldId]));
    }
  }
  function toggleSection(secId) {
    if (visibleSectionIds.indexOf(secId) >= 0) {
      setVisibleSectionIds(visibleSectionIds.filter(function(id) { return id !== secId; }));
    } else {
      setVisibleSectionIds(visibleSectionIds.concat([secId]));
    }
  }
  function setSectionFields(secId, on) {
    var ids = fields.filter(function(f){ return f.section_id === secId; }).map(function(f){ return f.id; });
    if (on) {
      var add = ids.filter(function(id){ return visibleFieldIds.indexOf(id) < 0; });
      setVisibleFieldIds(visibleFieldIds.concat(add));
      if (visibleSectionIds.indexOf(secId) < 0) setVisibleSectionIds(visibleSectionIds.concat([secId]));
    } else {
      setVisibleFieldIds(visibleFieldIds.filter(function(id){ return ids.indexOf(id) < 0; }));
      setVisibleSectionIds(visibleSectionIds.filter(function(id){ return id !== secId; }));
    }
  }
  function selectAll() {
    if (!formSchema) return;
    setVisibleFieldIds((formSchema.fields || []).map(function(f) { return f.id; }));
    setVisibleSectionIds((formSchema.sections || []).map(function(s) { return s.id; }));
  }

  // ── Priority tier editing ──────────────────────────────
  function setP(key, value) { setPriority(Object.assign({}, priority, key === null ? value : (function(){ var o = {}; o[key] = value; return o; })())); }
  function addTier() {
    setP('tiers', (priority.tiers || []).concat([{ label: '', match: 'in', values: [] }]));
  }
  function updateTier(i, key, value) {
    var t = (priority.tiers || []).slice();
    t[i] = Object.assign({}, t[i]); t[i][key] = value;
    setP('tiers', t);
  }
  function moveTier(i, delta) {
    var t = (priority.tiers || []).slice();
    var j = i + delta;
    if (j < 0 || j >= t.length) return;
    var tmp = t[i]; t[i] = t[j]; t[j] = tmp;
    setP('tiers', t);
  }
  function removeTier(i) {
    setP('tiers', (priority.tiers || []).filter(function(_, k){ return k !== i; }));
  }
  function releaseNext() {
    priorityAPI.release(formId, {}).then(function(r) {
      notify('Released through tier ' + (r.data.data.released_through + 1), {variant:'success'});
      setP('released_through', r.data.data.released_through);
      loadStatus();
    }).catch(function(e) {
      notify((e.response && e.response.data && e.response.data.error) || 'Could not release', {variant:'error'});
    });
  }

  // Back to the starting position: only the top tier visible, so the gate can be
  // walked forward again. Reviews already submitted are untouched — this only
  // changes what reviewers can currently open.
  function resetRelease() {
    if (!window.confirm('Reset the gate so only the first tier is visible again?\n\nReviews already submitted are kept. Reviewers with work open in a later tier will lose access to it until you release again.')) return;
    priorityAPI.release(formId, {released_through: 0}).then(function(r) {
      notify('Gate reset \u2014 only the first tier is visible', {variant:'success'});
      setP('released_through', r.data.data.released_through);
      loadStatus();
    }).catch(function(e) {
      notify((e.response && e.response.data && e.response.data.error) || 'Could not reset', {variant:'error'});
    });
  }

  // ── Review stages ──────────────────────────────────────
  function addStage() {
    setStages(stages.concat([{ id: uuidv4(), label: '', section_ids: [], criteria_ids: [],
      link_label: '', link_url: '' }]));
  }
  function updateStage(i, key, value) {
    var s = stages.slice();
    s[i] = Object.assign({}, s[i]); s[i][key] = value;
    setStages(s);
  }
  function moveStage(i, delta) {
    var s = stages.slice();
    var j = i + delta;
    if (j < 0 || j >= s.length) return;
    var tmp = s[i]; s[i] = s[j]; s[j] = tmp;
    setStages(s);
  }
  function removeStage(i) {
    setStages(stages.filter(function(_, k){ return k !== i; }));
  }
  function stagesFromSections() {
    setStages(sections.map(function(sec) {
      return { id: uuidv4(), label: sec.title, section_ids: [sec.id], criteria_ids: [],
        link_label: '', link_url: '' };
    }));
  }

  function openDeclLog() {
    declarationsAPI.log(formId).then(function(r) { setDeclLog(r.data.data); })
      .catch(function() { notify('Could not load the log', {variant:'error'}); });
  }

  function save() {
    criteriaAPI.save(formId, {
      criteria: criteria,
      anonymous_review: anonymousReview,
      review_open: reviewOpen,
      hide_empty_for_reviewers: hideEmpty,
      visible_field_ids: visibleFieldIds,
      visible_section_ids: visibleSectionIds,
      review_priority: priority,
      review_stages: stages,
      use_review_stages: useStages,
      declaration_enabled: declEnabled,
      declaration_title: declTitle,
      declaration_html: declHTML,
      score_formula: scoreFormula,
      score_formula_enabled: formulaEnabled,
    }).then(function() {
      notify('Review settings saved', {variant:'success'});
      loadStatus();
    }).catch(function(e) {
      notify((e.response && e.response.data && e.response.data.error) || 'Failed to save', {variant:'error'});
    });
  }

  var fields = formSchema ? (formSchema.fields || []).slice().sort(function(a,b){return a.order-b.order;}) : [];
  var sections = formSchema ? (formSchema.sections || []).slice().sort(function(a,b){return a.order-b.order;}) : [];

  // Labels that appear in more than one section are the parallel pathway copies.
  // Marking them makes it obvious which fields have a twin elsewhere in the form.
  var labelCounts = {};
  fields.forEach(function(f) {
    var k = (f.label || '').trim().toLowerCase();
    labelCounts[k] = (labelCounts[k] || 0) + 1;
  });
  function isDuplicate(f) { return labelCounts[(f.label || '').trim().toLowerCase()] > 1; }
  var duplicateCount = fields.filter(isDuplicate).length;

  var stagedCriteriaIds = {};
  stages.forEach(function(st) {
    (st.criteria_ids || []).forEach(function(id){ stagedCriteriaIds[id] = true; });
  });
  var unassignedCriteria = criteria.filter(function(c){ return !stagedCriteriaIds[c.id]; });
  var stagesWithoutSections = stages.filter(function(st){ return (st.section_ids || []).length === 0; });

  var E = React.createElement;

  return E(Box, null,
    E(Box, {sx:{display:'flex',justifyContent:'space-between',alignItems:'center',mb:3}},
      E(Typography, {variant:'h4'}, 'Review settings'),
      E(Button, {variant:'contained',startIcon:E(Save),onClick:save}, 'Save all settings')),

    // ── Review settings ──────────────────────────────────
    E(Card, {sx:{mb:3}},
      E(CardContent, null,
        E(Typography, {variant:'h6',sx:{mb:2}}, 'How reviewers see applications'),
        E(Grid, {container:true, spacing:2},
          E(Grid, {item:true, xs:12, md:4},
            E(FormControlLabel, {
              control: E(Switch, {checked:reviewOpen, onChange:function(e){setReviewOpen(e.target.checked);}}),
              label: E(Box, null,
                E(Typography, {variant:'body2',fontWeight:600}, 'Reviewing open'),
                E(Typography, {variant:'caption',color:'text.secondary'},
                  reviewOpen ? 'Reviewers can submit and edit' : 'Reviewers cannot make changes'))})),
          E(Grid, {item:true, xs:12, md:4},
            E(FormControlLabel, {
              control: E(Switch, {checked:anonymousReview, onChange:function(e){setAnonymousReview(e.target.checked);}}),
              label: E(Box, null,
                E(Typography, {variant:'body2',fontWeight:600}, 'Anonymous review'),
                E(Typography, {variant:'caption',color:'text.secondary'},
                  anonymousReview ? 'Name and email hidden' : 'Reviewer sees name and email'))})),
          E(Grid, {item:true, xs:12, md:4},
            E(FormControlLabel, {
              control: E(Switch, {checked:hideEmpty, onChange:function(e){setHideEmpty(e.target.checked);}}),
              label: E(Box, null,
                E(Typography, {variant:'body2',fontWeight:600}, 'Hide unanswered questions'),
                E(Typography, {variant:'caption',color:'text.secondary'},
                  hideEmpty ? 'Reviewers see only the pathway the applicant filled'
                            : 'Reviewers see every selected field, blank or not'))}))))),

    // ── Review priority ──────────────────────────────────
    E(Card, {sx:{mb:3}},
      E(CardContent, null,
        E(Box, {sx:{display:'flex',justifyContent:'space-between',alignItems:'center',mb:1}},
          E(Typography, {variant:'h6'}, 'Review order'),
          E(FormControlLabel, {
            control: E(Switch, {checked:!!priority.enabled,
              onChange:function(e){ setP('enabled', e.target.checked); }}),
            label:'Enabled'})),
        E(Typography, {variant:'body2',color:'text.secondary',sx:{mb:2}},
          'Sort the review queue by the answer to one question. Tiers are checked top to bottom and the first match wins, so the tier at the top is reviewed first.'),

        priority.enabled ? E(Box, null,
          E(Grid, {container:true, spacing:2, sx:{mb:2}},
            E(Grid, {item:true, xs:12, md:6},
              E(FormControl, {fullWidth:true, size:'small'},
                E(InputLabel, null, 'Question to sort by'),
                E(Select, {value:priority.field_id || '', label:'Question to sort by',
                  onChange:function(e){ setP('field_id', e.target.value); }},
                  E(MenuItem, {value:''}, E('em', null, 'Choose a question')),
                  fields.map(function(f) {
                    return E(MenuItem, {key:f.id, value:f.id},
                      f.label + (f.type === 'country' ? '  (country)' : ''));
                  })))),
            E(Grid, {item:true, xs:12, md:6},
              E(FormControl, {fullWidth:true, size:'small'},
                E(InputLabel, null, 'Behaviour'),
                E(Select, {value:priority.mode || 'sort', label:'Behaviour',
                  onChange:function(e){ setP('mode', e.target.value); }},
                  E(MenuItem, {value:'sort'}, 'Sort only \u2014 all tiers visible, higher tiers listed first'),
                  E(MenuItem, {value:'gate'}, 'Gate \u2014 later tiers stay hidden until released'))))),

          (priority.tiers || []).map(function(t, i) {
            return E(Card, {key:i, variant:'outlined', sx:{mb:1.5}},
              E(CardContent, {sx:{py:1.5}},
                E(Grid, {container:true, spacing:1.5, alignItems:'center'},
                  E(Grid, {item:true, xs:12, md:1},
                    E(Chip, {size:'small', label:'#' + (i + 1)})),
                  E(Grid, {item:true, xs:12, md:3},
                    E(TextField, {fullWidth:true, size:'small', label:'Tier name', value:t.label || '',
                      placeholder:'International',
                      onChange:function(e){ updateTier(i, 'label', e.target.value); }})),
                  E(Grid, {item:true, xs:12, md:3},
                    E(FormControl, {fullWidth:true, size:'small'},
                      E(InputLabel, null, 'Rule'),
                      E(Select, {value:t.match || 'in', label:'Rule',
                        onChange:function(e){ updateTier(i, 'match', e.target.value); }},
                        E(MenuItem, {value:'in'}, 'Answer is one of\u2026'),
                        E(MenuItem, {value:'not_in'}, 'Answer is anything except\u2026'),
                        E(MenuItem, {value:'rest'}, 'Everything else')))),
                  E(Grid, {item:true, xs:12, md:4},
                    t.match === 'rest'
                      ? E(Typography, {variant:'caption', color:'text.secondary'}, 'Catch-all \u2014 no values needed')
                      : E(TextField, {fullWidth:true, size:'small', label:'Values (comma separated)',
                          placeholder:'India', value:(t.values || []).join(', '),
                          onChange:function(e){
                            updateTier(i, 'values', e.target.value.split(',').map(function(s){ return s.trim(); })
                              .filter(function(s){ return s.length > 0; }));
                          }})),
                  E(Grid, {item:true, xs:12, md:1, sx:{display:'flex'}},
                    E(IconButton, {size:'small', onClick:function(){ moveTier(i, -1); }}, E(ArrowUpward, {fontSize:'inherit'})),
                    E(IconButton, {size:'small', onClick:function(){ moveTier(i, 1); }}, E(ArrowDownward, {fontSize:'inherit'})),
                    E(IconButton, {size:'small', color:'error', onClick:function(){ removeTier(i); }}, E(Delete, {fontSize:'inherit'}))))));
          }),

          E(Button, {size:'small', startIcon:E(Add), onClick:addTier}, 'Add tier'),

          (priority.tiers || []).length === 0 ? E(Alert, {severity:'info', sx:{mt:2}},
            'No tiers yet. A common setup is two: one for "anything except the host country", then a catch-all for the rest.') : null,

          status && status.tiers ? E(Box, {sx:{mt:3}},
            E(Typography, {variant:'subtitle2', sx:{mb:1}}, 'Where each tier stands'),
            E(Table, {size:'small'},
              E(TableHead, null,
                E(TableRow, null,
                  E(TableCell, null, 'Tier'),
                  E(TableCell, {align:'right'}, 'Applications'),
                  E(TableCell, {align:'right'}, 'Fully reviewed'),
                  E(TableCell, null, 'Visible to reviewers'))),
              E(TableBody, null,
                status.tiers.map(function(b) {
                  return E(TableRow, {key:b.index},
                    E(TableCell, null, b.label || '(unnamed)'),
                    E(TableCell, {align:'right'}, b.applications),
                    E(TableCell, {align:'right'}, b.applications_fully_reviewed + ' / ' + b.applications),
                    E(TableCell, null, E(Chip, {size:'small',
                      label: b.released ? 'Released' : 'Held',
                      color: b.released ? 'success' : 'default',
                      variant: b.released ? 'filled' : 'outlined'})));
                }))),
            priority.mode === 'gate' ? E(Box, {sx:{display:'flex', gap:1, mt:1.5, flexWrap:'wrap'}},
              E(Button, {size:'small', variant:'outlined', startIcon:E(LockOpen),
                disabled: (status.released_through || 0) >= status.tiers.length - 1,
                onClick:releaseNext}, 'Release next tier'),
              E(Button, {size:'small', variant:'outlined', color:'warning', startIcon:E(Replay),
                disabled: (status.released_through || 0) <= 0,
                onClick:resetRelease}, 'Reset release'),
              (status.released_through || 0) >= status.tiers.length - 1
                ? E(Typography, {variant:'caption', color:'text.secondary',
                    sx:{alignSelf:'center'}}, 'Every tier is released')
                : null) : null) : null

        ) : null)),

    // ── Review stages ────────────────────────────────────
    E(Card, {sx:{mb:3}},
      E(CardContent, null,
        E(Box, {sx:{display:'flex',justifyContent:'space-between',alignItems:'center',mb:1}},
          E(Typography, {variant:'h6'}, 'Review stages'),
          E(FormControlLabel, {
            control: E(Switch, {checked:useStages, onChange:function(e){ setUseStages(e.target.checked); }}),
            label:'Enabled'})),
        E(Typography, {variant:'body2',color:'text.secondary',sx:{mb:2}},
          'Split the review into steps. Each step shows chosen sections of the application alongside only the criteria that belong to them. The order here is the order reviewers work through. One stage holding everything gives you the all-at-once view.'),

        useStages ? E(Box, null,
          E(Box, {sx:{display:'flex',gap:1,mb:2,flexWrap:'wrap'}},
            E(Button, {size:'small', startIcon:E(Add), onClick:addStage}, 'Add stage'),
            sections.length > 0 ? E(Button, {size:'small', onClick:stagesFromSections},
              'Start from sections (' + sections.length + ')') : null),

          stages.map(function(st, i) {
            var stCriteria = st.criteria_ids || [];
            var stSections = st.section_ids || [];
            return E(Card, {key:st.id || i, variant:'outlined', sx:{mb:1.5}},
              E(CardContent, {sx:{py:1.5}},
                E(Grid, {container:true, spacing:1.5, alignItems:'center'},
                  E(Grid, {item:true, xs:12, md:1},
                    E(Chip, {size:'small', label:'Step ' + (i + 1)})),
                  E(Grid, {item:true, xs:12, md:3},
                    E(TextField, {fullWidth:true, size:'small', label:'Stage name', value:st.label || '',
                      placeholder:'Program proposal',
                      onChange:function(e){ updateStage(i, 'label', e.target.value); }})),
                  E(Grid, {item:true, xs:12, md:3.5},
                    E(FormControl, {fullWidth:true, size:'small'},
                      E(InputLabel, null, 'Sections shown'),
                      E(Select, {multiple:true, value:stSections, input:E(OutlinedInput, {label:'Sections shown'}),
                        renderValue:function(sel){
                          return sel.map(function(id){
                            var s = sections.filter(function(x){ return x.id === id; })[0];
                            return s ? s.title : id;
                          }).join(', ');
                        },
                        onChange:function(e){ updateStage(i, 'section_ids', e.target.value); }},
                        sections.map(function(sec) {
                          return E(MenuItem, {key:sec.id, value:sec.id},
                            E(Checkbox, {size:'small', checked:stSections.indexOf(sec.id) >= 0}),
                            E(ListItemText, {primary:sec.title}));
                        })))),
                  E(Grid, {item:true, xs:12, md:3.5},
                    E(FormControl, {fullWidth:true, size:'small'},
                      E(InputLabel, null, 'Criteria scored here'),
                      E(Select, {multiple:true, value:stCriteria, input:E(OutlinedInput, {label:'Criteria scored here'}),
                        renderValue:function(sel){
                          return sel.map(function(id){
                            var c = criteria.filter(function(x){ return x.id === id; })[0];
                            return c ? (c.name || '(unnamed)') : id;
                          }).join(', ');
                        },
                        onChange:function(e){ updateStage(i, 'criteria_ids', e.target.value); }},
                        criteria.map(function(c) {
                          return E(MenuItem, {key:c.id, value:c.id},
                            E(Checkbox, {size:'small', checked:stCriteria.indexOf(c.id) >= 0}),
                            E(ListItemText, {primary:c.name || '(unnamed criterion)'}));
                        })))),
                  E(Grid, {item:true, xs:12, md:1, sx:{display:'flex'}},
                    E(IconButton, {size:'small', onClick:function(){ moveStage(i, -1); }}, E(ArrowUpward, {fontSize:'inherit'})),
                    E(IconButton, {size:'small', onClick:function(){ moveStage(i, 1); }}, E(ArrowDownward, {fontSize:'inherit'})),
                    E(IconButton, {size:'small', color:'error', onClick:function(){ removeStage(i); }}, E(Delete, {fontSize:'inherit'}))),

                  // Optional reference link, shown under this stage's title on the review screen
                  E(Grid, {item:true, xs:12, md:4},
                    E(TextField, {fullWidth:true, size:'small', label:'Link text (optional)',
                      placeholder:'Scoring rubric', value:st.link_label || '',
                      onChange:function(e){ updateStage(i, 'link_label', e.target.value); }})),
                  E(Grid, {item:true, xs:12, md:7},
                    E(TextField, {fullWidth:true, size:'small', label:'Link URL (optional)',
                      placeholder:'https://meta.wikimedia.org/wiki/\u2026', value:st.link_url || '',
                      error: !!st.link_url && !/^https?:\/\//i.test(st.link_url),
                      helperText: (!!st.link_url && !/^https?:\/\//i.test(st.link_url))
                        ? 'Must start with http:// or https://'
                        : 'Reviewers see this beside the stage name',
                      onChange:function(e){ updateStage(i, 'link_url', e.target.value); }})))));
          }),

          stages.length === 0 ? E(Alert, {severity:'info'},
            'No stages yet. "Start from sections" gives you one stage per section to edit from.') : null,

          unassignedCriteria.length > 0 ? E(Alert, {severity:'warning', sx:{mt:2}},
            unassignedCriteria.length + ' criterion(s) are not in any stage — ' +
            unassignedCriteria.map(function(c){ return c.name || '(unnamed)'; }).join(', ') +
            '. They will appear in the last stage so they can still be scored.') : null,

          stagesWithoutSections.length > 0 ? E(Alert, {severity:'warning', sx:{mt:2}},
            'Some stages have no sections selected, so reviewers will see nothing to read in them.') : null,

          E(Alert, {severity:'info', sx:{mt:2}},
            'A stage whose sections are entirely blank for a given applicant is skipped for that applicant, and its criteria are not required. That is what keeps the three pathways working — a Scholarship-only applicant never sees the Section 3 stage, and its criteria do not block submission.')
        ) : null)),

    // ── Visible fields ───────────────────────────────────
    E(Card, {sx:{mb:3}},
      E(CardContent, null,
        E(Box, {sx:{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:1,mb:1}},
          E(Typography, {variant:'h6'}, 'Fields visible to reviewers'),
          E(Box, {sx:{display:'flex',gap:1,alignItems:'center'}},
            E(Button, {size:'small',onClick:selectAll}, 'Select all'),
            E(Button, {size:'small',onClick:function(){setVisibleFieldIds([]);setVisibleSectionIds([]);}}, 'Clear all'),
            E(Chip, {label:visibleFieldIds.length+' of '+fields.length+' fields',size:'small',variant:'outlined'}))),

        duplicateCount > 0 ? E(Alert, {severity:'info', sx:{mb:2}},
          duplicateCount + ' fields share a label with a field in another section \u2014 marked "also in another section" below. ' +
          'These are the parallel copies used for pathways. Select all of them: with "Hide unanswered questions" on, each reviewer only sees the copy the applicant actually filled.') : null,

        sections.length > 0 ? sections.map(function(sec) {
          var secFields = fields.filter(function(f){return f.section_id===sec.id;});
          var secChecked = visibleSectionIds.indexOf(sec.id) >= 0;
          var allOn = secFields.length > 0 && secFields.every(function(f){ return visibleFieldIds.indexOf(f.id) >= 0; });
          return E(Box, {key:sec.id, sx:{mb:2,pl:1,borderLeft:'3px solid',borderColor:secChecked?'primary.main':'divider'}},
            E(Box, {sx:{display:'flex',alignItems:'center',gap:1,flexWrap:'wrap'}},
              E(FormControlLabel, {
                control: E(Checkbox, {checked:secChecked, onChange:function(){toggleSection(sec.id);}}),
                label: E(Typography, {variant:'subtitle2',fontWeight:600}, sec.title)}),
              E(Button, {size:'small', onClick:function(){ setSectionFields(sec.id, !allOn); }},
                allOn ? 'Deselect section fields' : 'Select section fields'),
              E(Typography, {variant:'caption', color:'text.secondary'}, secFields.length + ' fields')),
            E(Box, {sx:{pl:4}},
              secFields.map(function(f) {
                return E(Box, {key:f.id, sx:{display:'flex',alignItems:'center',gap:1}},
                  E(FormControlLabel, {sx:{alignItems:'center'},
                    control: E(Checkbox, {checked:visibleFieldIds.indexOf(f.id)>=0, size:'small',
                      onChange:function(){toggleField(f.id);}}),
                    label: E(Typography, {variant:'body2', component:'span'},
                      f.label + (f.type==='file_upload'?' (file)':''))}),
                  isDuplicate(f) ? E(Tooltip, {title:'The same question label appears in another section \u2014 a pathway copy'},
                    E(Chip, {size:'small', variant:'outlined', color:'warning', label:'also in another section'})) : null);
              })));
        }) : fields.map(function(f) {
          return E(Box, {key:f.id},
            E(FormControlLabel, {sx:{alignItems:'center'},
              control: E(Checkbox, {checked:visibleFieldIds.indexOf(f.id)>=0, size:'small',
                onChange:function(){toggleField(f.id);}}),
              label: E(Typography, {variant:'body2', component:'span'}, f.label)}));
        }))),

    // ── Scoring criteria ─────────────────────────────────
    // ── Reviewer declaration ─────────────────────────────
    E(Card, {sx:{mb:3}},
      E(CardContent, null,
        E(Box, {sx:{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:1,mb:1}},
          E(Typography, {variant:'h6'}, 'Reviewer declaration'),
          E(Box, {sx:{display:'flex',alignItems:'center',gap:1}},
            E(Chip, {size:'small', variant:'outlined', label:'Version ' + declVersion}),
            E(Button, {size:'small', onClick:openDeclLog}, 'Acceptance log'),
            E(FormControlLabel, {
              control: E(Switch, {checked:declEnabled,
                onChange:function(e){ setDeclEnabled(e.target.checked); }}),
              label:'Enabled'}))),
        E(Typography, {variant:'body2',color:'text.secondary',sx:{mb:2}},
          'Shown to reviewers on this form before they can open anything. They must answer "Yes, I agree" to continue; answering "No" signs them out and they are asked again next time. Enforced on the server, so the queue stays hidden either way.'),

        declEnabled ? E(Box, null,
          E(TextField, {fullWidth:true, size:'small', label:'Dialog heading',
            placeholder:'Conflict of interest declaration', value:declTitle, sx:{mb:2},
            onChange:function(e){ setDeclTitle(e.target.value); }}),
          E(TextField, {fullWidth:true, multiline:true, rows:10, label:'Declaration (HTML)',
            value:declHTML,
            placeholder:'<p>By continuing you confirm that\u2026</p>\n<ul><li>\u2026</li></ul>',
            helperText:'Paragraphs, lists, headings, links and tables are supported. Scripts and event handlers are stripped before display.',
            onChange:function(e){ setDeclHTML(e.target.value); }, sx:{mb:2,
              '& textarea':{fontFamily:'monospace', fontSize:13}}}),

          declHTML.trim() ? E(Box, null,
            E(Typography, {variant:'subtitle2', sx:{mb:1}}, 'Preview'),
            E(Paper, {variant:'outlined', sx:{p:2, mb:2,
              '& p':{mb:1.5}, '& ul, & ol':{pl:3, mb:1.5}, '& li':{mb:0.5}},
              dangerouslySetInnerHTML:{__html: declHTML}})) : null,

          E(Alert, {severity:'warning'},
            'Changing the wording bumps the version, and everyone who already agreed is asked again. Enabling this on a form where reviewing is under way will stop reviewers until they accept.')
        ) : null)),

    E(Typography, {variant:'h6',sx:{mb:1}}, 'Scoring criteria'),
    E(Typography, {variant:'body2', color:'text.secondary', sx:{mb:2}},
      'Slider, number buttons, number entry and "choose one" produce a score and count towards the weighted total. Yes/No and text types capture a written response instead — useful for a question to carry into selection or programme design. Max score and weight are ignored by the text types.'),

    criteria.map(function(c, idx) {
      return E(Card, {key:c.id, sx:{mb:2}},
        E(CardContent, null,
          E(Grid, {container:true, spacing:2, alignItems:'center'},
            E(Grid, {item:true, xs:12, md:3},
              E(TextField, {fullWidth:true, label:'Criterion name', value:c.name,
                onChange:function(e){update(idx,'name',e.target.value);}})),
            E(Grid, {item:true, xs:12, md:3},
              E(TextField, {fullWidth:true, label:'Description', value:c.description || '',
                onChange:function(e){update(idx,'description',e.target.value);}})),
            E(Grid, {item:true, xs:6, md:1.5},
              E(TextField, {fullWidth:true, label:'Max score', type:'number', value:c.max_score,
                onChange:function(e){update(idx,'max_score',parseInt(e.target.value)||0);}})),
            E(Grid, {item:true, xs:6, md:1.5},
              E(TextField, {fullWidth:true, label:'Weight', type:'number', inputProps:{step:0.1}, value:c.weight,
                onChange:function(e){update(idx,'weight',parseFloat(e.target.value)||1);}})),
            E(Grid, {item:true, xs:6, md:2},
              E(FormControl, {fullWidth:true, size:'small'},
                E(InputLabel, null, 'Answer type'),
                E(Select, {value:c.scoring_type||'slider', label:'Answer type',
                  onChange:function(e){update(idx,'scoring_type',e.target.value);}},
                  E(MenuItem, {value:'slider'}, 'Slider (score)'),
                  E(MenuItem, {value:'radio'}, 'Number buttons (score)'),
                  E(MenuItem, {value:'number'}, 'Number entry (score)'),
                  E(MenuItem, {value:'select'}, 'Choose one (score optional)'),
                  E(MenuItem, {value:'yes_no'}, 'Yes / No'),
                  E(MenuItem, {value:'text'}, 'Short text'),
                  E(MenuItem, {value:'textarea'}, 'Long text')))),
            E(Grid, {item:true, xs:6, md:1},
              E(IconButton, {color:'error',onClick:function(){remove(idx);}}, E(Delete))),

            ['select'].indexOf(c.scoring_type) >= 0
              ? E(Grid, {item:true, xs:12, md:6},
                  E(TextField, {fullWidth:true, size:'small', multiline:true, rows:3,
                    label:'Choices (one per line)',
                    helperText:'Add "|3" after a choice to give it a score, e.g. Strong fit|3',
                    value:(c.options || []).join('\n'),
                    onChange:function(e){
                      update(idx, 'options', e.target.value.split('\n')
                        .map(function(s){ return s.trim(); })
                        .filter(function(s){ return s.length > 0; }));
                    }}))
              : null,

            ['text','textarea'].indexOf(c.scoring_type) >= 0
              ? E(Grid, {item:true, xs:12, md:6},
                  E(TextField, {fullWidth:true, size:'small', label:'Placeholder (optional)',
                    value:c.placeholder || '',
                    helperText:'Hint shown in the empty box',
                    onChange:function(e){update(idx,'placeholder',e.target.value);}}))
              : null,

            E(Grid, {item:true, xs:12, md:6},
              E(Box, {sx:{display:'flex', gap:2, flexWrap:'wrap'}},
                E(FormControlLabel, {
                  control:E(Switch, {size:'small', checked:c.required !== false,
                    onChange:function(e){update(idx,'required',e.target.checked);}}),
                  label:E(Typography, {variant:'caption'}, 'Required before submitting')}),
                E(FormControlLabel, {
                  control:E(Switch, {size:'small', checked:!!c.share_with_applicant,
                    onChange:function(e){update(idx,'share_with_applicant',e.target.checked);}}),
                  label:E(Typography, {variant:'caption'}, 'Intended for the applicant')}))))));
    }),

    E(Button, {variant:'outlined',startIcon:E(Add),onClick:addCriterion,sx:{mt:1}}, 'Add criterion'),

    E(Divider, {sx:{my:3}}),

    // ── Final score formula ──────────────────────────────
    E(Card, {sx:{mb:3}},
      E(CardContent, null,
        E(Box, {sx:{display:'flex',justifyContent:'space-between',alignItems:'center',mb:1}},
          E(Typography, {variant:'h6'}, 'Final score formula'),
          E(FormControlLabel, {
            control:E(Switch, {checked:formulaEnabled,
              onChange:function(e){ setFormulaEnabled(e.target.checked); }}),
            label:'Enabled'})),
        E(Typography, {variant:'body2',color:'text.secondary',sx:{mb:2}},
          'How the criteria above combine into one score per review. Without a formula the weighted sum is used, which is what reviewers see today.'),

        formulaEnabled ? E(Box, null,
          E(TextField, {fullWidth:true, label:'Formula', value:scoreFormula,
            placeholder:'({Relevance} * 2 + {Clarity}) / 3',
            onChange:function(e){ setScoreFormula(e.target.value); },
            sx:{mb:1.5, '& input':{fontFamily:'monospace'}}}),

          E(Typography, {variant:'caption', color:'text.secondary',
            sx:{display:'block', mb:0.5}}, 'Click to insert:'),
          E(Box, {sx:{display:'flex', gap:0.5, flexWrap:'wrap', mb:2}},
            criteria.filter(function(c){ return (c.name || '').trim(); }).map(function(c) {
              return E(Chip, {key:c.id, size:'small', variant:'outlined', label:c.name,
                onClick:function(){ setScoreFormula(scoreFormula + '{' + c.name + '}'); }});
            }),
            ['SUM','MAX','COUNT'].map(function(t) {
              return E(Chip, {key:t, size:'small', color:'primary', variant:'outlined', label:t,
                onClick:function(){ setScoreFormula(scoreFormula + t); }});
            })),

          (function() {
            if (!scoreFormula.trim()) {
              return E(Alert, {severity:'info'},
                'Operators + - * / and parentheses are supported. SUM is the weighted sum of all criteria, MAX the highest total they could reach, COUNT how many were scored.');
            }
            // Test against every criterion at its maximum.
            try {
              var vars = buildFormulaVars(criteria, function(id) {
                var c = criteria.filter(function(x){ return x.id === id; })[0];
                return c ? (c.max_score || 0) : 0;
              });
              var result = evaluateFormula(scoreFormula, vars);
              return E(Alert, {severity:'success'},
                'Valid. With every criterion at its maximum this gives ',
                E('strong', null, Number(result).toFixed(2)), '.');
            } catch (err) {
              return E(Alert, {severity:'error'}, 'Not valid: ' + err.message);
            }
          })(),

          E(Alert, {severity:'warning', sx:{mt:2}},
            'Saving a new formula does not rewrite scores already stored. Use Recalculate to apply it to every existing review on this form.'),
          E(Button, {size:'small', variant:'outlined', sx:{mt:1.5}, onClick:function() {
            if (!window.confirm('Recalculate the stored score for every review on this form using the saved formula?\n\nSave your changes first \u2014 this uses the formula currently stored, not what is on screen.')) return;
            priorityAPI.recalculate(formId).then(function(r) {
              notify(r.data.data.updated + ' review(s) recalculated', {variant:'success'});
            }).catch(function() { notify('Could not recalculate', {variant:'error'}); });
          }}, 'Recalculate existing reviews')
        ) : null)),

    E(Button, {variant:'contained',startIcon:E(Save),onClick:save}, 'Save all settings'),

    declLog ? E(Dialog, {open:true, onClose:function(){ setDeclLog(null); },
      maxWidth:'md', fullWidth:true},
      E(DialogTitle, null, 'Declaration responses'),
      E(DialogContent, {dividers:true},
        (declLog.responses || []).length === 0
          ? E(Alert, {severity:'info'}, 'Nobody has responded yet.')
          : E(Table, {size:'small'},
              E(TableHead, null,
                E(TableRow, null,
                  E(TableCell, null, 'Name'),
                  E(TableCell, null, 'Role'),
                  E(TableCell, null, 'Response'),
                  E(TableCell, {align:'right'}, 'Version'),
                  E(TableCell, null, 'When'),
                  E(TableCell, null, 'IP'))),
              E(TableBody, null,
                declLog.responses.map(function(x) {
                  return E(TableRow, {key:x.id},
                    E(TableCell, null, x.name, E(Typography, {variant:'caption',
                      color:'text.secondary', sx:{display:'block'}}, x.email)),
                    E(TableCell, null, x.role),
                    E(TableCell, null, E(Chip, {size:'small', label:x.action,
                      color: x.action === 'accepted' ? 'success' : 'error'})),
                    E(TableCell, {align:'right'}, x.version),
                    E(TableCell, null, dayjs(x.created_at).format('MMM D, YYYY HH:mm')),
                    E(TableCell, null, x.ip_address || '\u2014'));
                })))),
      E(DialogActions, null,
        E(Button, {onClick:function(){ setDeclLog(null); }}, 'Close'))) : null);
}
